import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiCpu, FiUsers, FiTrendingUp, FiCalendar, FiArrowRight, FiCheckCircle } = FiIcons;

const AIInterventionsPanel = ({ interventions = [] }) => {
  const getIcon = (type) => {
    switch (type.toLowerCase()) {
      case 'workshop': return FiUsers;
      case 'leadership coaching': return FiCpu;
      case 'conflict mediation': return FiCheckCircle;
      default: return FiCalendar;
    }
  };

  return (
    <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden shadow-sm">
      <div className="p-6 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between bg-indigo-50/30 dark:bg-indigo-900/10">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-indigo-600 rounded-lg shadow-indigo-200 dark:shadow-none shadow-lg">
            <SafeIcon icon={FiCpu} className="text-white w-5 h-5" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-slate-900 dark:text-white leading-tight">AI Recommended Interventions</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400">Automatisk genererte tiltak basert på sanntidsdata</p>
          </div>
        </div>
        <span className="hidden md:block px-3 py-1 bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 text-xs font-bold rounded-full uppercase tracking-wider">
          {interventions.length} Aktive Forslag
        </span>
      </div>

      <div className="p-6 space-y-4">
        {interventions.map((item, idx) => (
          <motion.div
            key={item.id}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: idx * 0.1 }}
            className="group relative p-5 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 hover:border-indigo-300 dark:hover:border-indigo-700 transition-all hover:shadow-md"
          >
            <div className="flex flex-col lg:flex-row lg:items-start gap-5">
              {/* Type & Icon */}
              <div className="flex-shrink-0 flex lg:flex-col items-center gap-3">
                <div className={`p-3 rounded-xl ${
                  item.priority === 'high' ? 'bg-rose-100 text-rose-600 dark:bg-rose-900/30 dark:text-rose-400' : 'bg-indigo-100 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400'
                }`}>
                  <SafeIcon icon={getIcon(item.intervention_type)} className="w-6 h-6" />
                </div>
                <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400 lg:text-center">
                  {item.intervention_type}
                </span>
              </div>

              {/* Content */}
              <div className="flex-grow space-y-3">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h4 className="text-base font-bold text-slate-900 dark:text-white group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors">
                      {item.title}
                    </h4>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs font-semibold text-slate-600 dark:text-slate-300">Målgruppe:</span>
                      <span className="px-2 py-0.5 bg-slate-100 dark:bg-slate-800 rounded text-[11px] text-slate-700 dark:text-slate-400">
                        {item.target_team}
                      </span>
                    </div>
                  </div>
                  {item.priority === 'high' && (
                    <span className="flex items-center gap-1 text-[10px] font-bold bg-rose-50 dark:bg-rose-900/20 text-rose-600 dark:text-rose-400 px-2 py-1 rounded border border-rose-100 dark:border-rose-900/30">
                      PRIORITERT
                    </span>
                  )}
                </div>

                <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed italic border-l-2 border-slate-200 dark:border-slate-700 pl-3">
                  "{item.reason}"
                </p>

                {/* Expected Impact */}
                <div className="flex flex-wrap gap-4 pt-2">
                  <div className="flex items-center gap-2">
                    <SafeIcon icon={FiTrendingUp} className="text-emerald-500 w-4 h-4" />
                    <span className="text-xs font-medium text-slate-500 dark:text-slate-400">Forventet effekt:</span>
                    <div className="flex gap-2">
                      {Object.entries(item.expected_impact).map(([key, val]) => (
                        <span key={key} className="text-[11px] font-bold text-emerald-600 dark:text-emerald-400">
                          {key.replace('_', ' ')} +{val}%
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Action */}
              <div className="flex-shrink-0 lg:self-center">
                <button className="w-full lg:w-auto flex items-center justify-center gap-2 px-6 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-bold transition-all shadow-sm hover:shadow-indigo-200">
                  Planlegg Tiltak <SafeIcon icon={FiArrowRight} />
                </button>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

export default AIInterventionsPanel;